import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";

function ImgDropzone(props) {
  const maxSize = 10485760;

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Only handle rejections if they're not due to too many files
    // or if we're not in a global drag-drop context
    if (rejectedFiles.length > 0) {
      // Filter out "too-many-files" errors
      const filteredRejections = rejectedFiles.filter(file => 
        !file.errors.some(error => error.code === 'too-many-files')
      );
      
      if (filteredRejections.length > 0) {
        props.rejectedFiles(filteredRejections);
      }
    }

    if (acceptedFiles.length > 0) {
      // Still handle the accepted files normally
      props.acceptedFiles(acceptedFiles);
    }
  }, []);

  const {
    isDragActive,
    getRootProps,
    getInputProps,
    isDragReject,
    acceptedFiles,
    rejectedFiles,
  } = useDropzone({
    onDrop,
    minSize: 0,
    maxSize,
    noClick: false,
    noKeyboard: true,
    accept: props.accept,
    multiple: props.multiple,
    // Prevent drag rejection due to too many files
    // This way individual dropzones won't reject multi-file drops
    getFilesFromEvent: async (event) => {
      // If this is a drop event and contains multiple files
      if (event.type === 'drop' && event.dataTransfer?.files.length > 1) {
        // For global drag and drop, don't process files here
        // Return an empty array to prevent rejection
        return [];
      }
      // For click-to-select or single file drops, use the standard behavior
      const fileList = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      return Array.from(fileList);
    }
  });

  const isFileTooLarge =
    rejectedFiles?.length > 0 && rejectedFiles[0].size > maxSize;

  // Don't show drag rejection for "too many files" errors
  const showDragReject = isDragReject && 
    !(rejectedFiles?.some(file => 
      file.errors.some(error => error.code === 'too-many-files')
    ));

  return (
    <div className="h-full w-full">
      <div {...getRootProps()} className="w-full h-full">
        <input
          {...getInputProps()}
          onClick={(e) => {
            e.target.value = null;
          }}
        />
        <div className="h-full w-full flex flex-col items-center">
          {isDragActive && !showDragReject ? (
            <div className="flex justify-center items-center mt-[10%]">
              <span className="sm:text-[16px] md:text-[20px] font-[400] text-[#8C8C8C] mb-1 font-nunito text-center">
                Drop your file here...
              </span>
            </div>
          ) : (
            !showDragReject && (
              <div className="h-full w-full flex flex-col justify-center items-center">
                {props.img}
              </div>
            )
          )}
          <span className="sm:text-[10px] md:text-[12px] font-[400] text-[#8C8C8C] font-nunito">
            {props.supportText}
          </span>
          <input
            {...getInputProps()}
            onClick={(e) => {
              e.target.value = null;
            }}
          />
          {showDragReject && (
            <div className="flex justify-center items-center mt-[10%]">
              <span className="sm:text-[16px] md:text-[20px] font-[400] text-[#8C8C8C] mb-1 font-nunito text-center">
                "File type must be png or jpeg!"
              </span>
            </div>
          )}
          {isFileTooLarge && (
            <div className="sm:text-[16px] md:text-[20px] font-[400] text-[#8C8C8C] mb-1 font-nunito text-center mt-[10%]">
              File is too large.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImgDropzone;